import boostsAPI from '@/api/sorority-wars/boostsAPI'

const responseErrors = error => {
  if (error.response && error.response.data && error.response.data.errors) {
    return error.response.data.errors
  }

  if (error.response && error.response.data && error.response.data.error) {
    return [error.response.data.error]
  }

  return ['Unable to complete boosts request.']
}

const state = {
  inventory: [],
  gifts: {
    incoming: [],
    sent: []
  },
  loading: false,
  giftsLoading: false,
  errors: []
}

const mutations = {
  setInventory (state, boosts) {
    state.inventory = boosts || []
  },
  setGifts (state, gifts) {
    state.gifts = {
      incoming: gifts.incoming || [],
      sent: gifts.sent || []
    }
  },
  setLoading (state, loading) {
    state.loading = loading
  },
  setGiftsLoading (state, loading) {
    state.giftsLoading = loading
  },
  setErrors (state, errors) {
    state.errors = errors || []
  },
  updateCount (state, { boost, count }) {
    if (!boost) return

    let found = false

    state.inventory = state.inventory.reduce((inventory, item) => {
      if (item.id !== boost.id) {
        inventory.push(item)
        return inventory
      }

      found = true

      if (count > 0) {
        inventory.push({ ...item, count })
      }

      return inventory
    }, [])

    if (!found && count > 0) {
      state.inventory.push({ ...boost, count })
    }
  },
  updateRemaining (state, { id, remaining }) {
    state.inventory = state.inventory.reduce((inventory, boost) => {
      if (boost.id !== id) {
        inventory.push(boost)
        return inventory
      }

      if (remaining > 0) {
        inventory.push({ ...boost, count: remaining })
      }

      return inventory
    }, [])
  },
  markGiftAccepted (state, gift) {
    if (!gift) return

    state.gifts.incoming = state.gifts.incoming.map(item => {
      if (item.id === gift.id) return gift

      return item
    })
  }
}

const actions = {
  getInventory (context) {
    context.commit('setLoading', true)
    context.commit('setErrors', [])

    return boostsAPI.getInventory()
      .then(res => {
        if (res.data.success) {
          context.commit('setInventory', res.data.boosts)
        } else {
          context.commit('setInventory', [])
          context.commit('setErrors', res.data.errors)
        }

        return res.data
      })
      .catch(error => {
        const errors = responseErrors(error)

        context.commit('setInventory', [])
        context.commit('setErrors', errors)

        return { success: false, errors }
      })
      .finally(() => {
        context.commit('setLoading', false)
      })
  },
  getGifts (context) {
    context.commit('setGiftsLoading', true)
    context.commit('setErrors', [])

    return boostsAPI.getGifts()
      .then(res => {
        if (res.data.success) {
          context.commit('setGifts', res.data)
        } else {
          context.commit('setGifts', { incoming: [], sent: [] })
          context.commit('setErrors', res.data.errors)
        }

        return res.data
      })
      .catch(error => {
        const errors = responseErrors(error)

        context.commit('setGifts', { incoming: [], sent: [] })
        context.commit('setErrors', errors)

        return { success: false, errors }
      })
      .finally(() => {
        context.commit('setGiftsLoading', false)
      })
  },
  useBoost (context, boostId) {
    return boostsAPI.use(boostId)
      .then(res => {
        if (res.data.success) {
          context.commit('updateRemaining', { id: boostId, remaining: res.data.remaining })
          context.dispatch('updateCurrentStats', res.data.stats, { root: true })
        }

        return res.data
      })
      .catch(error => {
        return { success: false, errors: responseErrors(error) }
      })
  },
  sendGift (context, { boostId, sentPlayerId }) {
    return boostsAPI.sendGift(boostId, sentPlayerId)
      .then(res => {
        if (res.data.success) {
          context.commit('updateRemaining', { id: boostId, remaining: res.data.remaining })
        }

        return res.data
      })
      .catch(error => {
        return { success: false, errors: responseErrors(error) }
      })
  },
  acceptGift (context, gift) {
    const giftId = typeof gift === 'object' ? gift.id : gift

    return boostsAPI.acceptGift(giftId)
      .then(res => {
        if (res.data.success) {
          const responseGift = res.data.gift || {}
          const sourceGift = typeof gift === 'object' ? gift : { id: giftId }
          const acceptedGift = responseGift.id
            ? res.data.gift
            : { ...sourceGift, accepted_at: responseGift.accepted_at || new Date().toISOString() }

          context.commit('markGiftAccepted', acceptedGift)
          context.commit('updateCount', { boost: acceptedGift.boost, count: res.data.count })
        }

        return res.data
      })
      .catch(error => {
        return { success: false, errors: responseErrors(error) }
      })
  }
}

const getters = {
  inventory: state => state.inventory,
  gifts: state => state.gifts
}

export default {
  state,
  mutations,
  actions,
  getters,
  namespaced: true
}
